import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/auth';
import { api } from '../services/api';
import { PageHeader } from '../components/PageHeader';

interface Tenant {
  id: string;
  tenant_id: string;
  name: string;
  slug: string;
  logo_url: string | null;
  primary_color: string;
  is_active: boolean;
  street: string | null;
  zip_code: string | null;
  city: string | null;
  country: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  tax_id: string | null;
  vat_id: string | null;
  meta_app_id: string | null;
  created_at: string;
  updated_at: string;
}

interface TenantFormData {
  name: string;
  slug: string;
  logo_url: string;
  primary_color: string;
  street: string;
  zip_code: string;
  city: string;
  country: string;
  phone: string;
  email: string;
  website: string;
  tax_id: string;
  vat_id: string;
  meta_app_id: string;
}

const emptyFormData: TenantFormData = {
  name: '',
  slug: '',
  logo_url: '',
  primary_color: '#3B82F6',
  street: '',
  zip_code: '',
  city: '',
  country: 'Deutschland',
  phone: '',
  email: '',
  website: '',
  tax_id: '',
  vat_id: '',
  meta_app_id: '',
};

function TenantModal({
  isOpen,
  onClose,
  tenant,
  onSave,
}: {
  isOpen: boolean;
  onClose: () => void;
  tenant: Tenant | null;
  onSave: (data: TenantFormData) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<TenantFormData>(emptyFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (tenant) {
      setFormData({
        name: tenant.name || '',
        slug: tenant.slug || '',
        logo_url: tenant.logo_url || '',
        primary_color: tenant.primary_color || '#3B82F6',
        street: tenant.street || '',
        zip_code: tenant.zip_code || '',
        city: tenant.city || '',
        country: tenant.country || 'Deutschland',
        phone: tenant.phone || '',
        email: tenant.email || '',
        website: tenant.website || '',
        tax_id: tenant.tax_id || '',
        vat_id: tenant.vat_id || '',
        meta_app_id: tenant.meta_app_id || '',
      });
    } else {
      setFormData(emptyFormData);
    }
    setError(null);
  }, [tenant, isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setError(t('admin:nameRequired'));
      return;
    }

    setIsSubmitting(true);
    setError(null);
    try {
      await onSave(formData);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('admin:errorOccurred'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[äöüß]/g, c => ({ 'ä': 'ae', 'ö': 'oe', 'ü': 'ue', 'ß': 'ss' })[c] || c)
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 px-6 py-4 rounded-t-lg">
          <h2 className="text-xl font-semibold text-white">
            {tenant ? t('admin:tenantManagement.editTenant') : t('admin:tenantManagement.newTenant')}
          </h2>
          {tenant && (
            <p className="text-indigo-100 text-sm font-mono">{tenant.tenant_id}</p>
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="px-6 py-3 bg-red-50 text-red-700 text-sm">{error}</div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* Grunddaten */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('admin:tenantManagement.name')} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    name: e.target.value,
                    slug: !tenant ? generateSlug(e.target.value) : formData.slug
                  });
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin:tenantManagement.slug')}</label>
              <input
                type="text"
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder={t('admin:tenantManagement.autoGenerated')}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin:tenantManagement.primaryColor')}</label>
              <div className="flex gap-2">
                <input
                  type="color"
                  value={formData.primary_color}
                  onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                  className="w-12 h-10 p-1 border border-gray-300 rounded-md cursor-pointer"
                />
                <input
                  type="text"
                  value={formData.primary_color}
                  onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono text-sm"
                  pattern="^#[0-9A-Fa-f]{6}$"
                />
              </div>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin:tenantManagement.logoUrl')}</label>
              <input
                type="url"
                value={formData.logo_url}
                onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="https://..."
              />
            </div>
          </div>

          {/* Kontakt */}
          <h4 className="text-sm font-medium text-gray-800 mb-3 pt-4 border-t border-gray-200">{t('admin:tenantManagement.contact')}</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin:tenantManagement.email')}</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin:tenantManagement.phone')}</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="+49 123 456789"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin:tenantManagement.website')}</label>
              <input
                type="url"
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="https://..."
              />
            </div>
          </div>

          {/* Adresse */}
          <h4 className="text-sm font-medium text-gray-800 mb-3 pt-4 border-t border-gray-200">{t('admin:tenantManagement.address')}</h4>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="md:col-span-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin:tenantManagement.street')}</label>
              <input
                type="text"
                value={formData.street}
                onChange={(e) => setFormData({ ...formData, street: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin:tenantManagement.postalCode')}</label>
              <input
                type="text"
                value={formData.zip_code}
                onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin:tenantManagement.city')}</label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin:tenantManagement.country')}</label>
              <input
                type="text"
                value={formData.country}
                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>

          {/* Steuerdaten */}
          <h4 className="text-sm font-medium text-gray-800 mb-3 pt-4 border-t border-gray-200">{t('admin:tenantManagement.taxData')}</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin:tenantManagement.taxId')}</label>
              <input
                type="text"
                value={formData.tax_id}
                onChange={(e) => setFormData({ ...formData, tax_id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin:tenantManagement.vatId')}</label>
              <input
                type="text"
                value={formData.vat_id}
                onChange={(e) => setFormData({ ...formData, vat_id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="DE..."
              />
            </div>
          </div>

          {/* Meta Integration */}
          <h4 className="text-sm font-medium text-gray-800 mb-3 pt-4 border-t border-gray-200">{t('admin:tenantManagement.metaIntegration')}</h4>
          <div className="grid grid-cols-1 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin:tenantManagement.metaAppId')}</label>
              <input
                type="text"
                value={formData.meta_app_id}
                onChange={(e) => setFormData({ ...formData, meta_app_id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono text-sm"
                placeholder="123456789012345"
              />
              <p className="text-xs text-gray-500 mt-1">
                {t('admin:tenantManagement.metaAppIdNote')}
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              {t('admin:cancel')}
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              {isSubmitting ? t('admin:saving') : t('admin:save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function TenantCard({
  tenant,
  onEdit,
  onDeactivate,
}: {
  tenant: Tenant;
  onEdit: (tenant: Tenant) => void;
  onDeactivate: (id: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className={`bg-white rounded-lg shadow p-5 ${!tenant.is_active ? 'opacity-60' : ''}`}>
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          {tenant.logo_url ? (
            <img
              src={tenant.logo_url}
              alt={tenant.name}
              className="w-12 h-12 rounded-lg object-cover"
            />
          ) : (
            <div
              className="w-12 h-12 rounded-lg flex items-center justify-center text-white font-bold text-lg"
              style={{ backgroundColor: tenant.primary_color }}
            >
              {tenant.name.charAt(0).toUpperCase()}
            </div>
          )}
          <div>
            <h3 className="font-semibold text-gray-900">{tenant.name}</h3>
            <p className="text-sm text-gray-500 font-mono">{tenant.tenant_id}</p>
          </div>
        </div>
        <span
          className={`px-2 py-1 text-xs rounded-full ${
            tenant.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}
        >
          {tenant.is_active ? t('admin:stats.active') : t('admin:stats.inactive')}
        </span>
      </div>

      <div className="space-y-2 text-sm text-gray-600 mb-4">
        {tenant.email && (
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            {tenant.email}
          </div>
        )}
        {tenant.phone && (
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
            {tenant.phone}
          </div>
        )}
        {(tenant.city || tenant.country) && (
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            {[tenant.city, tenant.country].filter(Boolean).join(', ')}
          </div>
        )}
        {tenant.website && (
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
            </svg>
            <a href={tenant.website} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline truncate">
              {tenant.website.replace(/^https?:\/\//, '')}
            </a>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
        <div className="flex items-center gap-2">
          <div
            className="w-4 h-4 rounded border border-gray-200"
            style={{ backgroundColor: tenant.primary_color }}
            title={tenant.primary_color}
          />
          <span className="text-xs text-gray-400 font-mono">{tenant.slug}</span>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => onEdit(tenant)}
            className="px-3 py-1 text-sm text-indigo-600 hover:bg-indigo-50 rounded"
          >
            {t('admin:edit')}
          </button>
          {tenant.is_active && (
            <button
              onClick={() => onDeactivate(tenant.id)}
              className="px-3 py-1 text-sm text-red-600 hover:bg-red-50 rounded"
            >
              {t('admin:tenantManagement.deactivate')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export function AdminTenantsPage() {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTenant, setEditingTenant] = useState<Tenant | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchTenants = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.get<{ success: boolean; data: Tenant[] }>('/tenants');
      setTenants(response.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('admin:errors.loadTenantsFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTenants();
  }, []);

  const handleCreate = async (data: TenantFormData) => {
    await api.post('/tenants', data);
    await fetchTenants();
  };

  const handleUpdate = async (data: TenantFormData) => {
    if (!editingTenant) return;
    await api.put(`/tenants/${editingTenant.id}`, data);
    await fetchTenants();
  };

  const handleDeactivate = async (id: string) => {
    if (!confirm(t('admin:confirmDelete.tenant'))) return;
    await api.delete(`/tenants/${id}`);
    await fetchTenants();
  };

  const filteredTenants = tenants.filter(t =>
    t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.tenant_id.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.slug.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (t.email && t.email.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Redirect non-admins
  if (user && user.role !== 'admin') {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="bg-white p-8 rounded-lg shadow text-center">
          <h1 className="text-xl font-semibold text-red-600 mb-4">{t('admin:accessDenied')}</h1>
          <p className="text-gray-600 mb-4">{t('admin:noPermission')}</p>
          <Link to="/" className="text-blue-600 hover:text-blue-800">
            {t('admin:backToDashboard')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <PageHeader
        icon={
          <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        }
        title={t('admin:tenantManagement.title')}
        subtitle={t('admin:tenantManagement.subtitle')}
        breadcrumbs={[
          { label: 'Dashboard', href: '/' },
          { label: t('admin:title'), href: '/admin' },
        ]}
        actions={
          <div className="flex gap-2">
            <Link
              to="/admin"
              className="px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-md text-sm font-medium hover:bg-gray-50"
            >
              {t('admin:users')}
            </Link>
            <Link
              to="/admin/tenants"
              className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium"
            >
              {t('admin:tenants')}
            </Link>
            <button
              onClick={() => {
                setEditingTenant(null);
                setIsModalOpen(true);
              }}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              {t('admin:tenantManagement.newTenant')}
            </button>
          </div>
        }
      />

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('admin:tenantManagement.searchTenants')}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-2xl font-bold text-gray-900">{tenants.length}</div>
          <div className="text-sm text-gray-500">{t('admin:stats.total')}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-2xl font-bold text-green-600">{tenants.filter(t => t.is_active).length}</div>
          <div className="text-sm text-gray-500">{t('admin:stats.active')}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-2xl font-bold text-indigo-600">{tenants.filter(t => t.meta_app_id).length}</div>
          <div className="text-sm text-gray-500">{t('admin:tenantManagement.withMetaIntegration')}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-2xl font-bold text-gray-400">{tenants.filter(t => !t.is_active).length}</div>
          <div className="text-sm text-gray-500">{t('admin:stats.inactive')}</div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-6 px-4 py-3 bg-red-50 text-red-700 rounded-lg">{error}</div>
      )}

      {/* Content */}
      {isLoading ? (
        <div className="text-center py-12 text-gray-500">{t('admin:loading')}</div>
      ) : filteredTenants.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            {t('admin:empty.tenants')}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchQuery ? t('admin:empty.tryOtherSearch') : t('admin:empty.tenantsCreate')}
          </p>
          {!searchQuery && (
            <button
              onClick={() => setIsModalOpen(true)}
              className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
            >
              {t('admin:tenantManagement.createTenant')}
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTenants.map(tenant => (
            <TenantCard
              key={tenant.id}
              tenant={tenant}
              onEdit={(t) => {
                setEditingTenant(t);
                setIsModalOpen(true);
              }}
              onDeactivate={handleDeactivate}
            />
          ))}
        </div>
      )}

      {/* Modal */}
      <TenantModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingTenant(null);
        }}
        tenant={editingTenant}
        onSave={editingTenant ? handleUpdate : handleCreate}
      />
    </div>
  );
}
